import React from 'react';
import { Button, Drawer, Flex, Input, Menu } from '@mantine/core';

import { ReactComponent as CheckIcon } from 'assets/check.svg';

import styles from './TagsMenu.module.scss';

interface Props {
  isOpen: boolean;
  triggerButton: React.ReactNode;
  tags: {
    id: number;
    name: string;
    selected: boolean;
  }[];
  onSubmitTagSelect: (selectedTag: number) => Promise<void>;
  onSubmitTagUnselect: (unselectedTag: number) => Promise<void>;
  onSubmitTagCreation: (tagName: string) => Promise<void>;
  onSubmitTagEdit: (editedTags: { id: number, name: string }[], deletedTags: number[]) => Promise<void>;
  onClose: () => void;
}

const TagsMenu = ({ isOpen, triggerButton, tags, onSubmitTagSelect, onSubmitTagUnselect, onSubmitTagCreation, onSubmitTagEdit, onClose }: Props) => {
  const [isCreateNewTagDrawerOpened, setIsCreateNewTagDrawerOpened] = React.useState(false);
  const [newTagName, setNewTagName] = React.useState('');

  const [isEditDrawerOpened, setIsEditDrawerOpened] = React.useState(false);
  const [editedTags, setEditedTags] = React.useState<{ [id: number]: string }>({});
  const [markedForDeletionTagIds, setMarkedForDeletionTagIds] = React.useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(false);

  const handleOpenChange = (opened: boolean) => {
    if (!opened) {
      onClose();
    }
  };

  const handleClickOnTag = (index: number) => () => {
    try {
      setIsSubmitting(true);

      if (tags[index].selected) {
        onSubmitTagUnselect(tags[index].id);
      } else {
        onSubmitTagSelect(tags[index].id);
      }

      setSubmitError(false);
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateNewTagDrawer = () => {
    setIsCreateNewTagDrawerOpened(true);
  };

  const openEditDrawer = () => {
    const initialEditedTags: { [id: number]: string } = {};
    tags.forEach((tag) => {
      initialEditedTags[tag.id] = tag.name;
    });
    setEditedTags(initialEditedTags);
    setIsEditDrawerOpened(true);
  };

  const handleTagInputChange = (id: number, value: string) => {
    setEditedTags((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleTagCreateSubmit = async () => {
    const newTag = newTagName.trim();

    if (!newTag) return;

    try {
      setIsSubmitting(true);
      setSubmitError(false);

      await onSubmitTagCreation(newTag);
      setNewTagName('');
      setIsCreateNewTagDrawerOpened(false);
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTagEditSubmit = async () => {
    const updatedTags = Object.entries(editedTags)
      .map(([id, name]) => ({ id: Number(id), name }))
      .filter(tag => markedForDeletionTagIds.indexOf(tag.id) === -1);

    if (updatedTags.length === 0 && markedForDeletionTagIds.length === 0) {
      setIsEditDrawerOpened(false);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(false);

      await onSubmitTagEdit(updatedTags, markedForDeletionTagIds);

      setIsEditDrawerOpened(false);
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const markTagForDeletion = (id: number) => () => {
    setMarkedForDeletionTagIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter(tagId => tagId !== id);
      }
      return [...prev, id];
    });
  };

  React.useEffect(() => {
    const reset = () => {
      setNewTagName('');
    };

    if (!isOpen) {
      reset();
    }
  }, [isOpen, tags]);

  return (
    <>
      <Menu
        shadow="md"
        width={350}
        closeOnItemClick={false}
        classNames={{
          dropdown: styles.dropdown,
          label: styles.menuLabel,
          item: styles.menuItem,
        }}
        opened={isOpen && !isCreateNewTagDrawerOpened && !isEditDrawerOpened}
        onChange={handleOpenChange}
      >
        <Menu.Target>
          {triggerButton}
        </Menu.Target>

        <Menu.Dropdown>
          <div className={styles.menuHeader}>
            <Menu.Label>Tag Account as:</Menu.Label>
            <Button
              type="button"
              onClick={onClose}
              classNames={{ root: styles.doneButton }}
            >
              DONE
            </Button>
          </div>
          <div className={styles.selectTagArea}>
            {tags.map((tag, index) => (
              <Menu.Item
                key={tag.id}
                leftSection={<CheckIcon style={{ opacity: tag.selected ? 1 : 0 }} />}
                onClick={handleClickOnTag(index)}
                disabled={isSubmitting}
              >
                {tag.name}
              </Menu.Item>
            ))}
          </div>

          <Menu.Divider />

          <div className={styles.actionsArea}>
            <Menu.Item
              className={styles.menuActionItem}
              onClick={openCreateNewTagDrawer}
            >
              Create New Tag +
            </Menu.Item>
            <Menu.Item
              className={styles.menuActionItem}
              onClick={openEditDrawer}
            >
              Edit/Delete Tags &gt;
            </Menu.Item>
          </div>
        </Menu.Dropdown>
      </Menu>

      <Drawer
        position="bottom"
        size={220}
        opened={isCreateNewTagDrawerOpened}
        onClose={() => setIsCreateNewTagDrawerOpened(false)}
        title="Create New Tag"
        classNames={{
          content: styles.drawer,
          title: styles.drawerTitle,
        }}
      >
        <Input
          placeholder="Tag 1"
          value={newTagName}
          onChange={(event) => setNewTagName(event.currentTarget.value)}
          classNames={{ input: styles.input }}
        />

        <Flex justify="center" align="center" gap={10} mt={30}>
          <Button
            variant="subtle"
            onClick={() => setNewTagName('')}
            classNames={{ root: styles.cancelButton }}
            disabled={isSubmitting}
          >
            Clear
          </Button>
          <Button
            onClick={handleTagCreateSubmit}
            disabled={newTagName.trim() === ''}
            classNames={{ root: styles.submitButton }}
            loading={isSubmitting}
            loaderProps={{ type: 'dots' }}
          >
            Create
          </Button>
        </Flex>
      </Drawer>

      <Drawer
        position="bottom"
        size="sm"
        opened={isEditDrawerOpened}
        onClose={() => setIsEditDrawerOpened(false)}
        title="Delete or Modify Tags"
        classNames={{
          content: styles.drawer,
          title: styles.drawerTitle,
          header: styles.drawerHeader,
          body: styles.drawerBody,
        }}
      >
        <Flex direction="column" gap={10} pr={10} mah={200} style={{ overflowY: 'auto' }}>
          {tags.map((tag) => (
            <Flex key={tag.id} justify="space-between" align="center" gap={10}>
              <Input
                value={editedTags[tag.id]}
                onChange={(event) => handleTagInputChange(tag.id, event.currentTarget.value)}
                classNames={{ input: styles.input }}
                style={{
                  flex: 1,
                  textDecoration: markedForDeletionTagIds.includes(tag.id) ? 'line-through' : 'none',
                  color: markedForDeletionTagIds.includes(tag.id) ? '#888' : 'inherit',
                  background: markedForDeletionTagIds.includes(tag.id) ? '#f5f5f5' : 'inherit',
                }}
                disabled={markedForDeletionTagIds.includes(tag.id)}
              />

              <Button
                variant="outline"
                size="xs"
                color="red"
                classNames={{ root: styles.deleteButton }}
                onClick={markTagForDeletion(tag.id)}
              >
                {markedForDeletionTagIds.includes(tag.id) ? 'UNDELETE' : 'DELETE'}
              </Button>
            </Flex>
          ))}
        </Flex>

        <Flex justify="center" align="center" gap={10} mt={30}>
          <Button
            variant="subtle"
            onClick={() => setIsEditDrawerOpened(false)}
            classNames={{ root: styles.cancelButton }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTagEditSubmit}
            classNames={{ root: styles.submitButton }}
          >
            Apply
          </Button>
        </Flex>
      </Drawer>
    </>
  );
}

export default TagsMenu;
